import * as React from "react";
import { useEffect } from "react";
import { isEqual } from "lodash-es";
import { FieldValues, useFormContext } from "react-hook-form";
import { FormWatcherCallback } from "duck";

interface WatcherProps<TValues extends FieldValues> {
  defaultValues: TValues;
  onChange: FormWatcherCallback<TValues>;
}

const Watcher = <TValues extends FieldValues>({
  defaultValues,
  onChange
}: WatcherProps<TValues>) => {
  const { watch, setValue } = useFormContext<TValues>();
  const timeout = React.useRef<number | null>(null);

  useEffect(() => {
    Object.entries(defaultValues).map(([name, value]) =>
      setValue(name as any, value, { shouldValidate: true, shouldDirty: true })
    );
  }, [defaultValues]);

  React.useEffect(() => {
    const sub = watch(values => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      const changedValues = Object.keys(values).reduce((acc, key) => {
        const currentValue = (values as any)[key];
        const initialValue = (defaultValues as any)[key];
        const dirty =
          typeof initialValue === "number"
            ? currentValue === null
              ? true
              : !isEqual(+currentValue, +initialValue)
            : !isEqual(currentValue, initialValue);

        return dirty ? { ...acc, [key]: (values as any)[key] } : acc;
      }, {});

      if (Object.keys(changedValues).length === 0) {
        return;
      }

      timeout.current = window.setTimeout(() => {
        onChange(changedValues);
      }, 400);
    });

    return () => {
      sub.unsubscribe();
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, [watch, defaultValues, onChange]);

  return null;
};

export default Watcher;
